import React, { useState } from 'react';
import { Layout, Menu, ConfigProvider, Input, Avatar, Typography, Button, message } from 'antd';
import { 
  DashboardOutlined, 
  TransactionOutlined, 
  CreditCardOutlined, 
  FileTextOutlined, 
  LineChartOutlined, 
  AimOutlined, 
  PlusOutlined, 
  SyncOutlined, 
  SettingOutlined, 
  QuestionCircleOutlined, 
  SearchOutlined, 
  BellOutlined, 
  MailOutlined,
  LogoutOutlined,
  EditOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import EstateList from '../estatelist/estatelist';
import EstateDetails from '../estatelist/estatedetails';
import DocumentsPage from '../documents/probatedocs';
import CalendarComponent from '../calendar/calendar';
import AssetPage from '../assetform/assetform';
import AssetsListPage from '../assets/assetlist';
import LiabilitiesListPage from '../liabilities/liabilities';
import CashFlowListPage from '../cashflows/cashflows';
import FamilyJewelsDashboardPage from '../familyjewels/familyjewels';
import { useGlobalContext } from '../../store/GlobalContext';
import RequireEstate from './RequireEstate';
import ParticipantsPage from '../participant/participant';
import TreeRenderer from '../familytree/tree';
import AccountPage from '../account/AccountPage';
import HelpPage from '../help/help';
import NotificationBell from '../notification/notificationBell';
import ResetPasswordPage from '../resetpassword/resetpassword';
import Cookies from 'js-cookie';
import EstateSelectorModal from '../estates/estateselector';
import FieldMappingPage from '../documents/FieldMappingPage';
import PdfVisualMapper from '../documents/PdfVisualMapper';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// Sidebar Header Logo and Search Container
const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
`;

// Logo component
const Logo = styled.div<{ collapsed: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: #3b82f6;
  display: ${props => (props.collapsed ? 'none' : 'block')};
`;

// Styled Avatar Container for User Info at Bottom of Sidebar
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  padding-bottom: 50px;
  bottom: 0;

`;

// Avatar styling
const CollapsibleAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;
`;

// Sidebar Menu Item Icon Style
const IconWrapper = styled.span<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => (props.collapsed ? '1.5rem' : '1rem')};
  margin-right: ${props => (props.collapsed ? '0' : '1rem')};
`;

// Top Navigation Search and Icons Container
const TopNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 2rem;
  background: #f4f6fb;
`;

const SearchInput = styled(Input)`
  width: 300px;
  border-radius: 24px;
  .ant-input-prefix {
    color: #888;
  }
`;

const IconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const siderStyle: React.CSSProperties = {
  overflow: 'auto',
  height: '100vh',
  position: 'fixed',
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
  paddingBottom: '50px',
  scrollbarWidth: 'thin',
  scrollbarGutter: 'stable',
  background: '#ffffff',
};

function Dashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Get the current route
  const { auth, selectedEstate, setAuth } = useGlobalContext();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const { user } = auth;

  // Logout Handler
  const handleLogout = () => {
    setAuth({
      ...auth,
      user: null,
      accessToken: null,
    });
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedEstate');

    //clear cookies
    Cookies.remove('user');
    Cookies.remove('accessToken');
    Cookies.remove('selectedEstate');
    
    message.success('Logged out successfully');
    navigate('/login');
  };

  // Define configuration arrays for different sections of the menu
  const mainMenuItems = [
    { key: '/estates', icon: <DashboardOutlined />, label: 'Estates' },
    { key: '/family-jewels', icon: <TransactionOutlined />, label: 'Family Jewels' },
    { key: '/probate-docs', icon: <CreditCardOutlined />, label: 'Probate Docs' },
    { key: '/calendar', icon: <FileTextOutlined />, label: 'Calendar' },
    { key: '/family-tree', icon: <AimOutlined />, label: 'Family Tree' },
    
  ];

  const quickLinksItems = [
    { key: '/assets', icon: <PlusOutlined />, label: 'Assets' },
    { key: '/liabilities', icon: <CreditCardOutlined />, label: 'Liabilities' },
    { key: '/cashflows', icon: <LineChartOutlined />, label: 'Cashflows' },
    { key: '/participants', icon: <AimOutlined />, label: 'Participants' },
  ];

  const bottomMenuItems = [
    { key: '/account', icon: <SettingOutlined />, label: 'Account' },
    { key: '/help-center', icon: <QuestionCircleOutlined />, label: 'Help Center' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', action: handleLogout },
  ];

  // Get the currently selected menu key based on the route
  const currentKey = location.pathname;

  return (
    <ConfigProvider>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={250}
          style={siderStyle}
        >
          {/* Sidebar Header */}
          <SidebarHeader>
            <Logo collapsed={collapsed}>Wzee</Logo>
            <Button
              type="text"
              icon={<SyncOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ marginLeft: 'auto', color: '#3b82f6' }}
            />
          </SidebarHeader>

          {/* Main Menu */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ border: 'none' }}>
            {mainMenuItems.map(({ key, icon, label }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => navigate(key)}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
            <Menu.Item
                key="change-active-estate"
                icon={<IconWrapper collapsed={collapsed}>{<EditOutlined />}</IconWrapper>}
                onClick={() => setModalVisible(true)}
              >
                {!collapsed && "Change Active Estate"}
            </Menu.Item>
            
          </Menu>

          {/* Quick Links Section */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ borderTop: '1px solid #e0e0e0', marginTop: '1rem' }}>
            {quickLinksItems.map(({ key, icon, label }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => navigate(key)}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
          </Menu>

          {/* Bottom Section */}
          <Menu mode="inline" selectedKeys={[currentKey]} style={{ borderTop: '1px solid #e0e0e0', marginTop: '1rem' }}>
            {bottomMenuItems.map(({ key, icon, label, action }) => (
              <Menu.Item
                key={key}
                icon={<IconWrapper collapsed={collapsed}>{icon}</IconWrapper>}
                onClick={() => {
                  if (action) {
                    action();
                  } else {
                    navigate(key);
                  }
                }}
              >
                {!collapsed && label}
              </Menu.Item>
            ))}
          </Menu>

          {/* User Info */}
          <UserInfo>
            <CollapsibleAvatar src="https://i.pravatar.cc/40" />
            {!collapsed && (
              <div>
                <Text strong>{user?.firstName || 'User'}</Text> <br />
                <Text type="secondary">{user?.email || 'user@example.com'}</Text>
              </div>
            )}
          </UserInfo>
        </Sider>

        <Layout style={{ marginLeft: collapsed ? 80 : 250 }}>
          <Header
            style={{
              background: '#f4f6fb',
              padding: 0,
              position: 'fixed',
              width: `calc(100% - ${collapsed ? 80 : 250}px)`,
              zIndex: 1,
            }}
          >
            <TopNav>
              <SearchInput placeholder="Search..." prefix={<SearchOutlined />} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* <BellOutlined /> */}
                <Button type="dashed" onClick={() => setModalVisible(true)} >Change Active Estate</Button>
                <NotificationBell /> 
                {/* account icon button */}
                <Button type="text" icon={<Avatar src="https://i.pravatar.cc/40" onClick={() => navigate('/account')}/>} />
              </div>
            </TopNav>
          </Header>

          <Content style={{ padding: '2rem', marginTop: 64, background: '#f4f6fb' }}>
          <EstateSelectorModal visible={modalVisible} onClose={() => setModalVisible(false)} />
            <Routes>
              {/* Wrap estate-specific routes with RequireEstate */}
              <Route
                path="/assets/*"
                element={
                  <RequireEstate>
                    <AssetsListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/cashflows/*"
                element={
                  <RequireEstate>
                    <CashFlowListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/liabilities/*"
                element={
                  <RequireEstate>
                    <LiabilitiesListPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs"
                element={
                  <RequireEstate>
                    <DocumentsPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs/:probateDocId/map-fields"
                element={
                  <RequireEstate>
                    <FieldMappingPage />
                  </RequireEstate>
                }
              />
              <Route
                path="/probate-docs/:probateDocId/visual-mapper"
                element={
                  <RequireEstate>
                    <PdfVisualMapper />
                  </RequireEstate>
                }
              />
              <Route
                path="/family-jewels/*"
                element={
                  <RequireEstate>
                    <FamilyJewelsDashboardPage />
                  </RequireEstate>
                }
              />
              <Route path="/dashboard" element={<EstateList />} />
              <Route path="/estates" element={<EstateList />} />
              <Route path="/estate/:estateId" element={<EstateDetails />} />
              <Route path="/calendar" element={<CalendarComponent isAdmin />} />
              <Route path="/assets/:assetId" element={<AssetPage />} />
              <Route path="/participants" element={<ParticipantsPage />} />
              <Route path="/family-tree" element={<TreeRenderer />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/help-center" element={<HelpPage />} />
              

            </Routes>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

export default Dashboard;
